import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiX, FiCopy, FiMail, FiGift } from "react-icons/fi";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalHeaderIcon,
  ModalHeaderContent,
  ModalHeaderTextWrapper,
  ModalSubtitle,
  InviteButton,
  MembersListSection,
  MembersListHeader,
  MembersList,
  MemberCard,
  MemberInfo,
  MemberName,
  MemberEmail,
} from "../Styles";
import {
  LinkBox,
  LinkActions,
  SecondaryLinkButton,
  ReferredHeader,
  CreditsTotal,
  CreditsEarned,
  EmptyText,
} from "./Styles";
import { useApi } from "../../../Common/Hooks/useApi";
import { Referral } from "../../../Common/Hooks/useApi/Schemas";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../Notifications";

interface ReferralModalProps {
  onClose: () => void;
}

/**
 * The register link a referrer shares.
 * @param referralCode - The business's referral code.
 * @returns The app's register page with the code, as the register flow reads it.
 */
export const referralLink = (referralCode: string): string =>
  `${window.location.origin}/register?ref=${encodeURIComponent(referralCode)}`;

export const ReferralModal = ({ onClose }: ReferralModalProps) => {
  const { t, i18n } = useTranslation();
  const { getReferralEndpoint } = useApi();
  const [referral, setReferral] = useState<Referral | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let isActive = true;
    getReferralEndpoint().then((result) => {
      if (!isActive) return;
      if (isErr(result)) {
        setFailed(true);
        return;
      }
      setReferral(result.value.referral);
    });

    return () => {
      isActive = false;
    };
  }, [getReferralEndpoint]);

  const formatCredits = (credits: number) => credits.toLocaleString(i18n.language);
  const formatDate = (date: Date) =>
    date.toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" });

  const link = referral ? referralLink(referral.referralCode) : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("success", t("common.sidebar.referralModal.linkCopied"))
      );
    } catch {
      // Clipboard permission denied or unavailable: the link is still on screen
    }
  };

  const mailto = `mailto:?subject=${encodeURIComponent(
    t("common.sidebar.referralModal.emailSubject")
  )}&body=${encodeURIComponent(
    t("common.sidebar.referralModal.emailBody", { link, interpolation: { escapeValue: false } })
  )}`;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()} style={{ maxWidth: "560px" }}>
        <ModalHeader>
          <ModalHeaderContent>
            <ModalHeaderIcon style={{ background: "rgba(0, 29, 48, 0.08)" }}>
              <FiGift size={20} color="#001d30" />
            </ModalHeaderIcon>
            <ModalHeaderTextWrapper>
              <ModalTitle>{t("common.sidebar.referralModal.title")}</ModalTitle>
              <ModalSubtitle>{t("common.sidebar.referralModal.subtitle")}</ModalSubtitle>
            </ModalHeaderTextWrapper>
          </ModalHeaderContent>
          <ModalCloseButton onClick={onClose}>
            <FiX size={20} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          {failed && <EmptyText>{t("common.sidebar.referralModal.loadError")}</EmptyText>}

          {referral && (
            <>
              <LinkBox data-testid="referral-link">{link}</LinkBox>
              <LinkActions>
                <InviteButton type="button" onClick={handleCopy}>
                  <FiCopy size={14} />
                  {t("common.sidebar.referralModal.copyLink")}
                </InviteButton>
                <SecondaryLinkButton href={mailto}>
                  <FiMail size={14} />
                  {t("common.sidebar.referralModal.sendByEmail")}
                </SecondaryLinkButton>
              </LinkActions>

              <MembersListSection>
                <ReferredHeader>
                  <MembersListHeader>
                    {t("common.sidebar.referralModal.referredTitle")}
                  </MembersListHeader>
                  {referral.creditsEarned > 0 && (
                    <CreditsTotal>
                      {t("common.sidebar.referralModal.creditsEarned", {
                        credits: formatCredits(referral.creditsEarned),
                      })}
                    </CreditsTotal>
                  )}
                </ReferredHeader>

                {referral.referredBusinesses.length === 0 ? (
                  <EmptyText>{t("common.sidebar.referralModal.empty")}</EmptyText>
                ) : (
                  <MembersList>
                    {referral.referredBusinesses.map((business) => (
                      <MemberCard key={`${business.name}-${business.registeredAt.toISOString()}`}>
                        <MemberInfo>
                          <MemberName>{business.name}</MemberName>
                          <MemberEmail>{formatDate(business.registeredAt)}</MemberEmail>
                        </MemberInfo>
                        {business.creditsEarned > 0 && (
                          <CreditsEarned>
                            {t("common.sidebar.referralModal.businessCredits", {
                              count: business.creditsEarned,
                              credits: formatCredits(business.creditsEarned),
                            })}
                          </CreditsEarned>
                        )}
                      </MemberCard>
                    ))}
                  </MembersList>
                )}
              </MembersListSection>
            </>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};
